import { HStack, Tooltip, Skeleton, IconButton, useDisclosure } from "@chakra-ui/react";
import CDS from "design-system";
import CaminoAnnounce from "components/Icon/CaminoAnnounce";
import { Text14 } from "components/Text";
import { tasksByStatusGroup } from "features/Resources/Task/helpers/filter";
import { useNewHireJourneyTasksQuery } from "features/Resources/Task/hooks";
import { NewHireJourney } from "models/v2/newHireJourney";
import SendRemindersQueueModal from "features/SendMessage/Components/SendRemindersQueueModal";
import { useReminderRows } from "features/SendMessage/hooks";
import { useCurrentUserQuery } from "features/User/hooks";
import { useMemo } from "react";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import OpenTasksPopover from "./OpenTasksPopover";

function InlineTaskStat({ openCount, overdueCount }: { openCount: number; overdueCount: number }) {
  if (openCount === 0) {
    return (
      <CDS.Text variant={CDS.Text.variant.bodyMedium} color={CDS.Color.text.secondary}>
        All clear 🎉
      </CDS.Text>
    );
  }

  return (
    <HStack spacing={2}>
      <CDS.Text variant={CDS.Text.variant.bodyMedium} color={CDS.Color.text.secondary}>
        {openCount} open
      </CDS.Text>
      <CDS.TaskStatusTag.OverdueCountBadge count={overdueCount} size="sm" />
    </HStack>
  );
}

interface ListProgressData {
  newHireJourney: NewHireJourney;
}


export default function ListProgress({ newHireJourney }: ListProgressData) {
  const reminderDisclosure = useDisclosure();
  const resolveHref = `${SHARED_ROUTES.myTasks}?newHireId=${newHireJourney.id}`;
  const { data: currentUser } = useCurrentUserQuery();
  const { data: tasks = [], isFetched } = useNewHireJourneyTasksQuery(newHireJourney?.id);
  const { active } = tasksByStatusGroup(tasks);

  // Calculate overdue tasks from active tasks
  const overdueCount = useMemo(() => active.filter((t) => t.isOverdue).length, [active]);

  // Open count is all active tasks
  const openCount = active.length;

  // All tasks completed when there are tasks and all are complete

  const {
    rows,
    youRow,
    excludedUserStakeholder: currentUserStakeholder,
    stakeholdersWithOpenTasks,
  } = useReminderRows(newHireJourney, { currentUserId: currentUser?.id });

  const hasStakeholdersToRemind = rows.length > 0;

  if (newHireJourney?.isPending !== false) {
    return (
      <HStack justify="space-between" py={3} px={4}>
        <Text14 color="gray.400">-</Text14>
      </HStack>
    );
  }

  return (
    <>
      <HStack justify="space-between" py={3} px={4}>
        <HStack spacing={4} minW="300px">
          {/* Task Status Tags */}
          {!isFetched ? (
            <Skeleton height="18px" width="120px" />
          ) : (
            <HStack spacing={4}>
              <OpenTasksPopover
                currentUserStakeholder={currentUserStakeholder}
                otherStakeholders={stakeholdersWithOpenTasks}
                resolveHref={resolveHref}
                onRemind={reminderDisclosure.onOpen}
              >
                <InlineTaskStat openCount={openCount} overdueCount={overdueCount} />
              </OpenTasksPopover>
            </HStack>
          )}

          {/* Remind button when there are stakeholders to remind (excluding current user) */}
          {hasStakeholdersToRemind && (
            <Tooltip label="Send Reminders" placement="top">
              <IconButton
                aria-label="Send reminders"
                icon={<CaminoAnnounce boxSize={4} />}
                size="xs"
                variant="outline"
                color="gray.600"
                borderColor="gray.300"
                bg="white"
                _hover={{ color: "gray.800", bg: "gray.100", borderColor: "gray.400" }}
                onClick={reminderDisclosure.onOpen}
              />
            </Tooltip>
          )}
        </HStack>
      </HStack>

      <SendRemindersQueueModal
        isOpen={reminderDisclosure.isOpen}
        onClose={reminderDisclosure.onClose}
        rows={rows}
        youRow={youRow}
        newHireJourneyId={newHireJourney.id}
        newHire={
          newHireJourney.newHire
            ? {
                fullName: newHireJourney.newHire.fullName,
                avatarUrl: newHireJourney.newHire.avatarUrl,
                title: newHireJourney.newHire.title,
              }
            : undefined
        }
      />
    </>
  );
}
